import { RefCallback, useCallback } from "react";

import { useResizeSensor } from "./useResizeSensor";

export interface ContainerQuery {
  minWidth?: number;
  maxWidth?: number;
  minHeight?: number;
  maxHeight?: number;
  // called whenever there is a resize with whether or not the query matches.
  // invoked very frequently.
  queryListener: (matches: boolean) => void;
}

/**
 * Watch a given element for resizes and invoke a query listener only when
 * the size changes beyond certain bounds.
 *
 * It is very important to both memoize the passed queries.
 *
 * This function is heavily inspired by the library [react-container-query](https://github.com/react-container-query/react-container-query)
 * with a couple key differences:
 * - Does not use any deprecated React APIs
 * - Causes fewer rerenders since it doesn't have internal state (both this hook and useResizeSensor)
 * - Smaller bundle size since we don't depend on a polyfil
 *
 * @param queries list of container query
 *
 * @returns ref callback that must be attached to the watched element
 */
export function useContainerQuery(
  ...queries: ContainerQuery[]
): RefCallback<HTMLElement> {
  const onResize = useCallback(
    ([entry]: ResizeObserverEntry[]) => {
      const {
        contentRect: { height, width },
      } = entry;

      for (const query of queries) {
        const {
          maxHeight = Infinity,
          maxWidth = Infinity,
          minHeight = 0,
          minWidth = 0,
          queryListener,
        } = query;

        queryListener(
          minWidth <= width &&
            width <= maxWidth &&
            minHeight <= height &&
            height <= maxHeight,
        );
      }
    },
    // we directly use the passed query list to avoid needing to worry about the reference
    // eslint-disable-next-line react-hooks/exhaustive-deps
    queries,
  );

  return useResizeSensor({ onResize });
}
